import React, { useState, useEffect } from "react";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";

interface IntroAnimationProps {
  onAnimationComplete: () => void;
}

const IntroAnimation: React.FC<IntroAnimationProps> = ({ onAnimationComplete }) => {
  const [phase, setPhase] = useState<"initial" | "rising" | "complete">("initial");

  useEffect(() => {
    // Démarrage après un court délai
    const startTimer = setTimeout(() => {
      setPhase("rising");
    }, 300);

    // Fin de l'animation (3s de montée + 0.5s de pause)
    const completeTimer = setTimeout(() => {
      setPhase("complete");
      onAnimationComplete();
    }, 4000);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(completeTimer);
    };
  }, [onAnimationComplete]);

  const isRising = phase === "rising" || phase === "complete";

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      style={{
        // Fond qui s'éclaircit progressivement
        background: isRising 
          ? "hsl(42 67% 97%)" // Couleur finale claire
          : "hsl(42 30% 75%)", // Départ plus sombre (aube)
        transition: "background 3s cubic-bezier(0.16, 0.75, 0.4, 1)",
      }}
    >
      {/* Overlay d'assombrissement initial qui disparaît */}
      <div 
        className="absolute inset-0 pointer-events-none transition-opacity"
        style={{
          background: "linear-gradient(180deg, hsl(92 20% 20% / 0.3) 0%, hsl(92 20% 25% / 0.2) 50%, hsl(45 30% 40% / 0.15) 100%)",
          opacity: isRising ? 0 : 1,
          transitionDuration: "2500ms",
          transitionTimingFunction: "ease-out",
        }}
      />

      {/* Rayonnement solaire principal - diffusion sur toute la page */}
      <div 
        className="absolute pointer-events-none"
        style={{
          top: "50%",
          left: "50%",
          width: "200vw",
          height: "200vh",
          background: `
            radial-gradient(ellipse at center, 
              hsl(45 68% 47% / 0.20) 0%, 
              hsl(45 60% 50% / 0.12) 15%, 
              hsl(45 50% 55% / 0.06) 30%, 
              hsl(45 40% 60% / 0.02) 50%,
              transparent 70%
            )
          `,
          opacity: isRising ? 1 : 0,
          transform: isRising 
            ? "translate(-50%, -50%) scale(1.3)" 
            : "translate(-50%, -50%) scale(0.6)",
          transition: "all 3s cubic-bezier(0.16, 0.75, 0.4, 1)",
        }}
      />

      {/* Rayons de lumière très subtils partant du centre */}
      <div 
        className="absolute pointer-events-none"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "150vw",
          height: "150vh",
          background: `
            conic-gradient(from 0deg at 50% 50%, 
              transparent 0deg, 
              hsl(45 68% 60% / 0.04) 10deg, 
              transparent 20deg,
              hsl(45 68% 60% / 0.03) 35deg, 
              transparent 50deg,
              hsl(45 68% 60% / 0.04) 65deg, 
              transparent 80deg,
              hsl(45 68% 60% / 0.03) 95deg, 
              transparent 110deg,
              hsl(45 68% 60% / 0.04) 125deg, 
              transparent 140deg,
              hsl(45 68% 60% / 0.03) 155deg, 
              transparent 170deg,
              hsl(45 68% 60% / 0.04) 185deg, 
              transparent 200deg,
              hsl(45 68% 60% / 0.03) 215deg, 
              transparent 230deg,
              hsl(45 68% 60% / 0.04) 245deg, 
              transparent 260deg,
              hsl(45 68% 60% / 0.03) 275deg, 
              transparent 290deg,
              hsl(45 68% 60% / 0.04) 305deg, 
              transparent 320deg,
              hsl(45 68% 60% / 0.03) 335deg, 
              transparent 350deg,
              hsl(45 68% 60% / 0.04) 360deg
            )
          `,
          opacity: isRising ? 1 : 0,
          filter: "blur(40px)",
          transition: "opacity 2.5s ease-out 0.5s",
        }}
      />

      {/* Container principal avec le système de masque */}
      <div className="relative flex flex-col items-center">
        
        {/* Halo lumineux enveloppant toute l'animation (logo + texte) */}
        <div 
          className="absolute pointer-events-none"
          style={{
            top: "50%",
            left: "50%",
            width: "600px",
            height: "500px",
            background: `
              radial-gradient(ellipse at center, 
                hsl(45 68% 47% / 0.25) 0%, 
                hsl(45 68% 50% / 0.15) 20%, 
                hsl(45 60% 55% / 0.08) 40%, 
                hsl(45 50% 60% / 0.03) 60%,
                transparent 80%
              )
            `,
            opacity: isRising ? 1 : 0,
            transform: isRising 
              ? "translate(-50%, -50%) scale(1.2)" 
              : "translate(-50%, -50%) scale(0.7)",
            filter: "blur(30px)",
            transition: "all 3s cubic-bezier(0.16, 0.75, 0.4, 1)",
          }}
        />
        
        {/* Zone du logo avec masque - le logo monte de derrière le texte */}
        <div 
          className="relative flex justify-center"
          style={{
            // Le conteneur a une hauteur qui permet au logo de monter - responsive
            height: isRising ? "clamp(100px, 18vw, 180px)" : "0px",
            marginBottom: isRising ? "clamp(8px, 2vw, 16px)" : "0px",
            overflow: "hidden",
            transition: "all 3s cubic-bezier(0.16, 0.75, 0.4, 1)",
          }}
        >
          {/* Logo sans halo individuel - le halo global suffit */}
          <div 
            className="relative"
            style={{
              transform: isRising ? "translateY(0)" : "translateY(100%)",
              opacity: isRising ? 1 : 0,
              transition: "all 3s cubic-bezier(0.16, 0.75, 0.4, 1)",
            }}
          >
            {/* Le logo lui-même */}
            <img
              src={brandLogo}
              alt="Logo REGARD DE NATURE"
              className="relative w-28 sm:w-36 md:w-44 h-auto object-contain"
            />
          </div>
        </div>

        {/* Texte REGARD DE NATURE - toujours visible et fixe */}
        <div className="relative text-center z-10">
          <div className="font-cinzel font-bold tracking-wider">
            <span 
              className="text-4xl sm:text-5xl md:text-6xl block leading-tight"
              style={{ color: "hsl(45 68% 47%)" }}
            >
              {BRAND.name.line1}
            </span>
            <span 
              className="text-4xl sm:text-5xl md:text-6xl block leading-tight mt-1"
              style={{ color: "hsl(92 20% 33%)" }}
            >
              {BRAND.name.line2}
            </span>
          </div>
          
          {/* Baseline - apparaît synchronisée avec la montée du logo */}
          <p 
            className="mt-5 text-xs sm:text-sm md:text-base font-montserrat font-semibold uppercase tracking-widest"
            style={{
              opacity: isRising ? 1 : 0,
              transform: isRising ? "translateY(0)" : "translateY(10px)",
              transition: "all 1.5s ease-out 1.5s",
            }}
          >
            <span style={{ color: "hsl(92 20% 33%)" }}>{BRAND.baseline.parts.text1}</span>
            <span style={{ color: "hsl(45 68% 47%)" }}> {BRAND.baseline.parts.separator} </span>
            <span style={{ color: "hsl(92 20% 33%)" }}>{BRAND.baseline.parts.text2}</span>
          </p>
        </div>
      </div>

      {/* Ligne d'horizon subtile */}
      <div 
        className="absolute left-0 right-0 pointer-events-none"
        style={{
          top: "55%",
          height: "1px",
          background: `linear-gradient(90deg, 
            transparent 0%, 
            hsl(45 68% 47% / 0.2) 20%, 
            hsl(45 68% 47% / 0.3) 50%, 
            hsl(45 68% 47% / 0.2) 80%, 
            transparent 100%
          )`,
          opacity: isRising ? 0.6 : 0,
          transition: "opacity 2s ease-out 1s",
        }}
      />

      {/* Lueur chaude en bas de l'écran (reflet) */}
      <div 
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: "30%",
          background: `linear-gradient(180deg, 
            transparent 0%, 
            hsl(45 50% 80% / 0.1) 50%, 
            hsl(45 60% 70% / 0.15) 100%
          )`,
          opacity: isRising ? 1 : 0,
          transition: "opacity 2.5s ease-out 0.5s",
        }}
      />
    </div>
  );
};

export default IntroAnimation;
